import { supabase } from '@/integrations/supabase/client';
import { devLog } from '@/utils/logger';
import type { 
  Payslip, 
  PayslipsResponse, 
  PayslipWithLineItems, 
  PayslipDetailsResponse, 
  PayslipLineItem,
  LineItemBreakdown 
} from './types';

export const payslipService = {
  /**
   * Get all payslips for a specific profile
   */
  getPayslips: async (profileId: string): Promise<PayslipsResponse> => {
    const { data, error } = await supabase
      .from('payslips')
      .select('*')
      .eq('profile_id', profileId)
      .order('start_date', { ascending: false });

    if (error) {
      console.error('[payslipService] Error fetching payslips:', error);
      throw new Error(`Failed to fetch payslips: ${error.message}`);
    }

    const payslips = data || [];
    
    // Calculate summary data
    const summary = {
      totalEarnings: payslips.reduce((total, payslip) => total + Number(payslip.gross_pay), 0),
      totalHours: payslips.reduce((total, payslip) => total + Number(payslip.total_hours), 0),
      payslipCount: payslips.length
    };

    return { payslips, summary };
  },

  /**
   * Get detailed payslip with line items
   */
  getPayslipDetails: async (payslipId: string): Promise<PayslipDetailsResponse> => {
    devLog('[payslipService] Fetching payslip details for:', payslipId);
    
    // Fetch payslip with line items
    const { data: payslipData, error: payslipError } = await supabase
      .from('payslips')
      .select(`
        *,
        line_items:payslip_line_items(*)
      `)
      .eq('id', payslipId)
      .single();

    if (payslipError) {
      console.error('[payslipService] Error fetching payslip details:', payslipError);
      throw new Error(`Failed to fetch payslip details: ${payslipError.message}`);
    }

    // Get previous payslip for comparison
    const { data: previousPayslip } = await supabase
      .from('payslips')
      .select('*')
      .eq('profile_id', payslipData.profile_id)
      .lt('start_date', payslipData.start_date)
      .order('start_date', { ascending: false })
      .limit(1)
      .maybeSingle();

    // Calculate YTD summary
    const startOfYear = new Date(new Date(payslipData.start_date).getFullYear(), 0, 1);
    const { data: ytdPayslips } = await supabase
      .from('payslips')
      .select('gross_pay, net_pay, total_hours')
      .eq('profile_id', payslipData.profile_id)
      .gte('start_date', startOfYear.toISOString())
      .lte('start_date', payslipData.start_date);

    const yearToDateSummary = {
      total_gross: ytdPayslips?.reduce((sum, p) => sum + Number(p.gross_pay || 0), 0) || 0,
      total_net: ytdPayslips?.reduce((sum, p) => sum + Number(p.net_pay || 0), 0) || 0,
      total_hours: ytdPayslips?.reduce((sum, p) => sum + Number(p.total_hours || 0), 0) || 0,
      periods_count: ytdPayslips?.length || 0
    };

    // Calculate enhanced totals from line items
    const lineItems = payslipData.line_items || [];
    const calculated_totals = payslipService.calculatePayslipTotals(lineItems as PayslipLineItem[]);

    const payslipWithLineItems: PayslipWithLineItems = {
      ...payslipData,
      line_items: lineItems as PayslipLineItem[],
      calculated_totals
    };

    return {
      payslip: payslipWithLineItems,
      previous_payslip: previousPayslip || undefined,
      year_to_date_summary: yearToDateSummary
    };
  },

  /**
   * Get line items for a specific payslip
   */
  getPayslipLineItems: async (payslipId: string): Promise<PayslipLineItem[]> => {
    devLog('[payslipService] Fetching line items for payslip:', payslipId);
    
    const { data, error } = await supabase
      .from('payslip_line_items')
      .select('*')
      .eq('payslip_id', payslipId)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('[payslipService] Error fetching line items:', error);
      throw new Error(`Failed to fetch line items: ${error.message}`);
    }

    return data || [];
  },

  /**
   * Get line items breakdown by category
   */
  getLineItemBreakdown: async (payslipId: string): Promise<LineItemBreakdown> => {
    const lineItems = await payslipService.getPayslipLineItems(payslipId);
    
    return {
      earnings: lineItems.filter(item => item.category === 'earnings'),
      deductions: lineItems.filter(item => item.category === 'deductions'),
      taxes: lineItems.filter(item => item.category === 'taxes'),
      benefits: lineItems.filter(item => item.category === 'benefits')
    };
  },

  /**
   * Calculate enhanced payslip totals from line items
   */
  calculatePayslipTotals: (lineItems: PayslipLineItem[]) => {
    const earnings = lineItems
      .filter(item => item.category === 'earnings')
      .reduce((sum, item) => sum + Number(item.gross_amount || 0), 0);

    const deductions = lineItems
      .filter(item => ['deductions', 'taxes', 'benefits'].includes(item.category || ''))
      .reduce((sum, item) => sum + Number(item.gross_amount || 0), 0);

    const taxes = lineItems
      .filter(item => item.category === 'taxes')
      .reduce((acc, item) => {
        // Create a key from the description or rule name for any tax type
        const taxKey = item.description 
          ? item.description.replace(/\s+/g, '_').toLowerCase()
          : `tax_${item.compensation_rule_id}`;
        
        acc[taxKey] = Number(item.gross_amount || 0);
        return acc;
      }, {} as Record<string, number>);

    return {
      gross_earnings: earnings,
      total_deductions: deductions,
      net_pay: earnings - deductions,
      tax_breakdown: taxes,
      ytd_totals: {
        gross_earnings: 0, // Will be calculated from YTD query
        net_pay: 0,
        total_deductions: 0
      }
    };
  }
};
